import { Divider, mergeClasses } from '@fluentui/react-components';
import { useLazyLoadQuery } from '@nova/react';
import { format } from '@office-start/string-formatter-utility';
import React from 'react';
import { AppBarErrorBoundary } from './AppBarErrorBoundary';
import { FlexPanel } from './FlexPanel';
import { KeyNavDir } from './FlexPanel.types';
import { useFlyoutStyles } from './Flyout.styles';
import { useFlyoutContext } from './FlyoutContext';
import { searchQuery } from './FlyoutSearchResults.types';
export var SuggestionsAndSearchRenderer = function (_a) {
    var _b = _a.userInSearchMode, userInSearchMode = _b === void 0 ? true : _b, _c = _a.installedAppCount, installedAppCount = _c === void 0 ? 0 : _c, suggestedAppsSectionTitle = _a.suggestedAppsSectionTitle, suggestedAppsSectionAriaLabel = _a.suggestedAppsSectionAriaLabel, installedAppsAnnouncementMessage = _a.installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage = _a.suggestedAppsAnnouncementMessage, installedAndSuggestedAnnouncementMessage = _a.installedAndSuggestedAnnouncementMessage;
    var styles = useFlyoutStyles();
    var _d = useFlyoutContext(), userSearchQuery = _d.userSearchQuery, launchOptions = _d.launchOptions;
    var suggestedAppsPrivacyLinkText = launchOptions.suggestedAppsPrivacyLinkText, suggestedAppsTermsOfUseLinkText = launchOptions.suggestedAppsTermsOfUseLinkText, suggestedAppsPermissionsLinkText = launchOptions.suggestedAppsPermissionsLinkText, suggestedAppsAddButtonText = launchOptions.suggestedAppsAddButtonText, closeFlyout = launchOptions.closeFlyout, searchNoResultsTitle = launchOptions.searchNoResultsTitle, searchNoResultsMessage = launchOptions.searchNoResultsMessage, _e = launchOptions.customImportHandler, customImportHandler = _e === void 0 ? function (p) { return p; } : _e;
    // Lazy renderer for the Suggested App Tiles
    var LazySuggestedAppTileRenderer = React.useRef(React.lazy(function () {
        return customImportHandler(import(
        /* webpackChunkName: "appbar-control-suggestedapptile" */ './SuggestedAppTile'));
    }));
    var suggestedSearchResultsQuery = useLazyLoadQuery(searchQuery, {
        searchQuery: userSearchQuery,
    });
    var suggestedSearchResults = React.useMemo(function () {
        if (suggestedSearchResultsQuery.error) {
            throw suggestedSearchResultsQuery.error;
        }
        else if (!(suggestedSearchResultsQuery === null || suggestedSearchResultsQuery === void 0 ? void 0 : suggestedSearchResultsQuery.data)) {
            return undefined;
        }
        return suggestedSearchResultsQuery.data.m365Apps.edges;
    }, [suggestedSearchResultsQuery.data, suggestedSearchResultsQuery.error]);
    var buildSuggestedTilesFromTileData = React.useCallback(function (tileData) {
        var LazySuggestedAppTile = LazySuggestedAppTileRenderer.current;
        return tileData === null || tileData === void 0 ? void 0 : tileData.map(function (app, index) {
            return (React.createElement(React.Fragment, { key: app.node.id },
                React.createElement(AppBarErrorBoundary, null,
                    React.createElement(React.Suspense, { fallback: "" },
                        React.createElement(LazySuggestedAppTile, { m365App: app.node, privacyLabel: suggestedAppsPrivacyLinkText !== null && suggestedAppsPrivacyLinkText !== void 0 ? suggestedAppsPrivacyLinkText : '', termsOfUseLabel: suggestedAppsTermsOfUseLinkText !== null && suggestedAppsTermsOfUseLinkText !== void 0 ? suggestedAppsTermsOfUseLinkText : '', permissionsLabel: suggestedAppsPermissionsLinkText !== null && suggestedAppsPermissionsLinkText !== void 0 ? suggestedAppsPermissionsLinkText : '', addLabel: suggestedAppsAddButtonText !== null && suggestedAppsAddButtonText !== void 0 ? suggestedAppsAddButtonText : '', onClicked: closeFlyout }))),
                index < tileData.length - 1 && (React.createElement(Divider, { className: styles.dividerCommon }))));
        });
    }, [
        closeFlyout,
        styles.dividerCommon,
        suggestedAppsAddButtonText,
        suggestedAppsPermissionsLinkText,
        suggestedAppsPrivacyLinkText,
        suggestedAppsTermsOfUseLinkText,
    ]);
    var searchResultsAnnouncementMessage = React.useMemo(function () {
        var _a;
        return userSearchQuery ? (React.createElement(SearchResultsAnnouncementMessage, { installedAppCount: installedAppCount, suggestedAppCount: (_a = suggestedSearchResults === null || suggestedSearchResults === void 0 ? void 0 : suggestedSearchResults.length) !== null && _a !== void 0 ? _a : 0, installedAppsAnnouncementMessage: installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage: suggestedAppsAnnouncementMessage, installedAndSuggestedAnnouncementMessage: installedAndSuggestedAnnouncementMessage })) : undefined;
    }, [
        installedAndSuggestedAnnouncementMessage,
        installedAppCount,
        installedAppsAnnouncementMessage,
        suggestedAppsAnnouncementMessage,
        suggestedSearchResults === null || suggestedSearchResults === void 0 ? void 0 : suggestedSearchResults.length,
        userSearchQuery,
    ]);
    var suggestedSearchResultTiles = React.useMemo(function () {
        var _a;
        var suggestedAppTiles = (_a = buildSuggestedTilesFromTileData(suggestedSearchResults)) !== null && _a !== void 0 ? _a : [];
        return suggestedAppTiles.length > 0 ? (React.createElement(FlexPanel, { horizontal: false, className: styles.suggestedAppsWrapper, keyNavigationDirection: KeyNavDir.gridLinear, role: "listbox", "aria-label": suggestedAppsSectionAriaLabel !== null && suggestedAppsSectionAriaLabel !== void 0 ? suggestedAppsSectionAriaLabel : '', wrap: true },
            React.createElement(Divider, { className: mergeClasses(styles.dividerCommon, styles.resultsDivider) }, suggestedAppsSectionTitle),
            suggestedAppTiles)) : undefined;
    }, [
        buildSuggestedTilesFromTileData,
        suggestedSearchResults,
        styles.suggestedAppsWrapper,
        styles.dividerCommon,
        styles.resultsDivider,
        suggestedAppsSectionAriaLabel,
        suggestedAppsSectionTitle,
    ]);
    var noSuggestionsToShow = suggestedSearchResults && !suggestedSearchResults.length;
    // no results found so serve up an empty message
    if (userInSearchMode && installedAppCount === 0 && noSuggestionsToShow) {
        return (React.createElement(NoResultsContainer, { noResultsTitle: searchNoResultsTitle, noResultsMessage: searchNoResultsMessage, styles: styles }));
    }
    return (React.createElement(React.Fragment, null,
        suggestedSearchResultTiles,
        searchResultsAnnouncementMessage));
};
var NoResultsContainer = function (_a) {
    var noResultsTitle = _a.noResultsTitle, noResultsMessage = _a.noResultsMessage, styles = _a.styles;
    return (React.createElement(FlexPanel, { className: mergeClasses(styles.tilesEmptyStateContainer, styles.tilesContainerSearchEnabled) },
        React.createElement("div", { className: styles.tilesEmptyStateMessage, role: "status" },
            React.createElement("span", { className: styles.noResultsTitle }, noResultsTitle),
            React.createElement("span", { className: styles.noResultsMessage }, noResultsMessage))));
};
var SearchResultsAnnouncementMessage = function (_a) {
    var installedAppCount = _a.installedAppCount, suggestedAppCount = _a.suggestedAppCount, installedAppsAnnouncementMessage = _a.installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage = _a.suggestedAppsAnnouncementMessage, installedAndSuggestedAnnouncementMessage = _a.installedAndSuggestedAnnouncementMessage;
    var styles = useFlyoutStyles();
    var searchAnnouncementMessage = React.useMemo(function () {
        if (installedAppCount > 0 &&
            suggestedAppCount > 0 &&
            installedAndSuggestedAnnouncementMessage) {
            // announce installed and suggested app count
            return format(installedAndSuggestedAnnouncementMessage, installedAppCount.toString(), suggestedAppCount.toString());
        }
        else if (suggestedAppCount > 0 && suggestedAppsAnnouncementMessage) {
            // announce suggested app count
            return format(suggestedAppsAnnouncementMessage, suggestedAppCount.toString());
        }
        else if (installedAppCount > 0 && installedAppsAnnouncementMessage) {
            // announce installed app count
            return format(installedAppsAnnouncementMessage, installedAppCount.toString());
        }
        // don't announce anything if there are no results
        // the no results control will be rendered and announce the message there.
        return undefined;
    }, [
        installedAndSuggestedAnnouncementMessage,
        installedAppCount,
        installedAppsAnnouncementMessage,
        suggestedAppCount,
        suggestedAppsAnnouncementMessage,
    ]);
    return (React.createElement(React.Fragment, null, searchAnnouncementMessage && (React.createElement("div", { role: "status", className: styles.searchResultsStatusMessage }, searchAnnouncementMessage))));
};
//# sourceMappingURL=FlyoutSuggestionsSection.js.map